import cx from 'classnames';
import { t } from 'i18next';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Link } from './link';
import { Stack } from './stack/Stack';
import { RioSelect } from './presentational/select/Select';
import { useMemo } from 'react';

function NavButton(props: any) {
  const anchor = props.anchor || '';

  const to = useMemo(() => {
    if (props.scrollOnChange === false) {
      const actionPath = props.action.split('?')[0];
      const actionSearch = props.action.split('?')[1];

      return {
        pathname: actionPath,
        state: { disableScroll: true },
        hash: anchor,
        ...(actionSearch ? { search: actionSearch } : {})
      };
    }

    return `${props.action}${anchor}`;
  }, [props.action, anchor, props.scrollOnChange]);

  if (!props.enabled) {
    return <span className="rio-pagination--button rio-pagination--disabled">{props.children}</span>;
  }

  if (props.isFunctionAction) {
    return (
      <a className="rio-pagination--button" onClick={props.action}>
        {props.children}
      </a>
    );
  }

  return (
    <Link className="rio-pagination--button" to={to}>
      {props.children}
    </Link>
  );
}

export function NavButtons(props: any) {
  const anchor = props.anchor ? '#' + props.anchor : '';
  const pageSizeOptions = props.pageSizeOptions || [10, 25, 50, 100];

  const prevButton = (
    <NavButton
      enabled={props.prevEnabled}
      isFunctionAction={props.isFunctionAction}
      action={props.toPrev}
      anchor={anchor}
      scrollOnChange={props.scrollOnChange}
    >
      <i className="fa fa-long-arrow-left" /> &nbsp; {props.prevLabel || t('common:pagination.previous')}
    </NavButton>
  );

  const nextButton = (
    <NavButton
      enabled={props.nextEnabled}
      isFunctionAction={props.isFunctionAction}
      action={props.toNext}
      anchor={anchor}
      scrollOnChange={props.scrollOnChange}
    >
      {props.nextLabel || t('common:pagination.next')} &nbsp; <i className="fa fa-long-arrow-right" />
    </NavButton>
  );

  let firstButton = null;
  if (props.toFirst) {
    firstButton = (
      <NavButton
        enabled={props.firstEnabled}
        isFunctionAction={props.isFunctionAction}
        action={props.toFirst}
        anchor={anchor}
        scrollOnChange={props.scrollOnChange}
      >
        <i className="fa fa-fast-backward" />
        <span className="rio-hidden--small"> &nbsp; {props.firstLabel || t('common:pagination.first')}</span>
      </NavButton>
    );
  }

  let lastButton = null;
  if (props.toLast) {
    lastButton = (
      <NavButton
        enabled={props.lastEnabled}
        isFunctionAction={props.isFunctionAction}
        action={props.toLast}
        anchor={anchor}
        scrollOnChange={props.scrollOnChange}
      >
        <span className="rio-hidden--small">{props.lastLabel || t('common:pagination.last')} &nbsp; </span>
        <i className="fa fa-fast-forward" />
      </NavButton>
    );
  }

  let pageSizeChange = null;

  if (props.changePageSize && props.showSizeChanger) {
    pageSizeChange = (
      <RioSelect
        value={props.pageSize}
        onChange={props.changePageSize}
        options={pageSizeOptions}
        getOptionLabel={(pageSize) => `${pageSize} / ${t('common:pagination.rows-per-page')}`}
        size="small"
      />
    );
  }

  const tags = [];
  if (!props.isFunctionAction) {
    if (props.nextEnabled) {
      tags.push(<link key="prev" rel="next" href={props.toNext} />);
    }
    if (props.prevEnabled) {
      tags.push(<link key="next" rel="prev" href={props.toPrev} />);
    }
  }

  return (
    <Stack
      style={{
        gap: '1rem 0'
      }}
      spacingInRems={0}
      alignItems="center"
      className={cx(props.className)}
      wrap
    >
      {firstButton}
      {prevButton}
      {nextButton}
      {lastButton}
      {pageSizeChange}
      {tags.length > 0 && <Helmet>{tags}</Helmet>}
    </Stack>
  );
}
